import { DragAndDropUploadData } from "@/components/DragAndDropFields";
import { DragAndDropAcceptFileType } from "@/components/DragAndDropFields";
import { Button, Dropdown, Label, Option } from "@fluentui/react-components";
import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { useAppAccount } from "../../../../AppProvider/AppAccountProvider";

import {
  postAdminCreateFrontendAddonVersion,
  postAdminEditFrontendAddonVersion,
} from "@/api/admin/frontendAddons";
import {
  postCreateFrontendAddonVersion,
  postEditFrontendAddonVersion,
} from "@/api/frontendAddons";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { useFrontendAddons } from "@/components/InOrganizationProvider/FrontendAddonsProvider";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { FrontendAddonWithTargetVersionRaw } from "@/domain/FrontendAddon";
import { useAppRouteParams } from "../../../../../AppRoutes";
import { DialogPhaseType, useDialog } from "../../../../Dialog/helpers/hooks";
import { LayoutTemplate } from "../../../../LayoutTemplate";
import { FrontendAddonFileField } from "../../../helpers/FrontendAddonFileField";
import { WarningMessageForProduction } from "../../../helpers/WarningMessageForProduction";
import cardTitleStyles from "../../../helpers/cardTitle.module.css";
import layoutStyles from "../../../helpers/formLayout.module.css";
import styles from "./index.module.css";

type VersionStrategy = "overwrite" | "new";

const versionStrategyLabels: Record<VersionStrategy, string> = {
  overwrite: "latestを上書き",
  new: "新規バージョン作成",
};

export const FrontendAddonVersionUpdate = ({
  isProd,
  returnPath,
  targetFrontendAddon,
}: {
  isProd: boolean;
  returnPath: string;
  targetFrontendAddon: FrontendAddonWithTargetVersionRaw;
}) => {
  const navigate = useNavigate();

  const { organizationId, frontendAddonId } = useAppRouteParams();
  const { isAADeveloperOrAbove } = useAppAccount();
  const { runReloadFrontendAddons } = useFrontendAddons();
  const { hasPermission } = usePermissions();
  const hasWritePermission =
    isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "write");
  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();

  const [dialogPhase, setDialogPhase] = useState<DialogPhaseType>(null);
  const { renderDialogs, isInProgress } = useDialog({
    dialogPhase,
    setDialogPhase,
  });

  const [existingType, setExistingType] = useState<
    DragAndDropAcceptFileType | undefined
  >();

  const [versionStrategy, setVersionStrategy] = useState<
    VersionStrategy | undefined
  >(undefined);

  const [uploadedZipFileList, setUploadedZipFileList] = useState<
    DragAndDropUploadData[]
  >([]);
  const runUpdateVersionFrontendAddon = useCallback(async () => {
    setDialogPhase("in_progress");

    try {
      if (versionStrategy === "overwrite") {
        isAADeveloperOrAbove
          ? await postAdminEditFrontendAddonVersion({
              frontendAddonId,
              FrontendAddonVersionId: targetFrontendAddon.latest_version,
              file: uploadedZipFileList[0].file,
            })
          : await postEditFrontendAddonVersion({
              organizationId,
              frontendAddonId,
              FrontendAddonVersionId: targetFrontendAddon.latest_version,
              file: uploadedZipFileList[0].file,
            });
      } else if (versionStrategy === "new") {
        isAADeveloperOrAbove
          ? await postAdminCreateFrontendAddonVersion({
              frontendAddonId,
              file: uploadedZipFileList[0].file,
            })
          : await postCreateFrontendAddonVersion({
              organizationId,
              frontendAddonId,
              file: uploadedZipFileList[0].file,
            });
      }
      await runReloadFrontendAddons();
      showSuccessNotification("フロントエンドアドオンを更新しました");
      navigate(returnPath);
    } catch (error) {
      showErrorNotification(
        "フロントエンドアドオンの更新に失敗しました",
        error
      );
    }
    setDialogPhase(null);
  }, [
    versionStrategy,
    uploadedZipFileList,
    organizationId,
    frontendAddonId,
    targetFrontendAddon,
    isAADeveloperOrAbove,
    navigate,
    returnPath,
    runReloadFrontendAddons,
    showSuccessNotification,
    showErrorNotification,
  ]);

  const isDisableForm =
    !hasWritePermission ||
    isInProgress ||
    targetFrontendAddon.is_disabled ||
    !targetFrontendAddon.is_editable;

  const renderVersionStrategyDescription = () => {
    if (versionStrategy === "overwrite") {
      return (
        <div className={styles.strategyDescription}>
          現在のlatestバージョンをアップロードしたファイルで上書きします。
          {isAADeveloperOrAbove
            ? "使用バージョンが「latest」の組織に即時反映されます。"
            : null}
        </div>
      );
    }
    if (versionStrategy === "new") {
      return (
        <div className={styles.strategyDescription}>
          新しいバージョンとして作成し、このバージョンが最新（latest）になります。現在のバージョンは履歴として残ります。
        </div>
      );
    }
    return null;
  };

  const renderFormBody = () => (
    <div className={layoutStyles.formField}>
      <div className={layoutStyles.inputContainer}>
        <Label required>バージョン更新方法</Label>
        <Dropdown
          className={styles.dropdown}
          placeholder="選択してください"
          value={versionStrategy ? versionStrategyLabels[versionStrategy] : ""}
          selectedOptions={versionStrategy ? [versionStrategy] : []}
          onOptionSelect={(_, data) => {
            setVersionStrategy(data.optionValue as VersionStrategy | undefined);
          }}
          disabled={isDisableForm}
        >
          <Option value="overwrite">{versionStrategyLabels.overwrite}</Option>
          <Option value="new">{versionStrategyLabels.new}</Option>
        </Dropdown>
        {renderVersionStrategyDescription()}
      </div>
      <FrontendAddonFileField
        uploadedFileList={uploadedZipFileList}
        setUploadedFileList={setUploadedZipFileList}
        existingType={existingType}
        setExistingType={setExistingType}
        isDisableForm={isDisableForm}
      />
    </div>
  );

  const isDisableSave =
    isDisableForm ||
    versionStrategy === undefined ||
    uploadedZipFileList?.length !== 1;

  const renderRightHeader = () => {
    return (
      <div className={layoutStyles.buttonContainer}>
        <Button
          appearance="primary"
          disabled={isDisableSave}
          onClick={() => setDialogPhase("confirm")}
        >
          更新
        </Button>
        <Button
          appearance="secondary"
          onClick={() => {
            navigate(returnPath);
          }}
          disabled={isInProgress}
        >
          キャンセル
        </Button>
      </div>
    );
  };

  return (
    <>
      <div className={layoutStyles.cardContainer}>
        <LayoutTemplate
          bodyElement={renderFormBody()}
          leftHeaderElement={
            <>
              "
              <span
                className={`${cardTitleStyles.cardTitle} ${styles.cardTitle}`}
                title={targetFrontendAddon.name}
              >
                {targetFrontendAddon.name}
              </span>
              " の更新
            </>
          }
          rightHeaderElement={renderRightHeader()}
        />
      </div>

      {renderDialogs({
        confirm: {
          element: isProd ? (
            <WarningMessageForProduction isEdit={true} />
          ) : (
            <>フロントエンドアドオンを更新しますか？</>
          ),
          title: "フロントエンドアドオン更新",
          buttonText: "更新",
          onClick: runUpdateVersionFrontendAddon,
        },
        inProgress: {
          title: "フロントエンドアドオンの更新中",
        },
      })}
    </>
  );
};
